import { colorsForProduct, getWsjColorArr, PIE_COLORS } from '../constants/colors.js';
import { getSizeById, getTargets } from '../constants/targets';
import { convertToRGBA } from '../../chart/data/processSeries';
import { getGrid } from './width';
import { ELECTION_COLORS, WSJ_VIDEO } from '../constants/colors-wsj.js';
import { PROD } from '../constants/environment';
import { sanitizeYValue } from '../constants/units';
import { copyObject } from './copyObject';
import { hasPrint } from './cropsUtil';

/**
 * @returns {object} ready to be consumed by the rendering engine.
 */
export default function renderOptions(
  activeDoc,
  el,
  width,
  size = null,
  publish = 'inset',
  skipUpdate = false
) {
  const { data } = activeDoc.chart;
  const categoriesAreDates =
    (data.firstColumnAsCategories && data.categoriesAreDates) ||
    (data.type === 'market' && activeDoc.chart.type !== 'bar') ||
    data.type === 'econ';
  const product = getProduct(activeDoc.chart.product);
  const print = size && size.indexOf('print') === 0;
  const isStaging = ENVIRONMENT !== PROD;
  const opts = {
    isStaging,
    skipUpdate,
    series: prepareRenderData(activeDoc.chart),
    categories: prepareCategories(data),
    annotations: data.items,
    legend: {
      enabled: data.enabled,
      layout: data.layout,
      showColorChips: data.showColorChips,
      itemsInOrder: data.itemsInOrder,
    },
    headings: {
      title: Object.assign({}, data.title),
      dek: Object.assign({}, data.dek),
      subdek: Object.assign({}, data.subdek),
      note: Object.assign({}, data.note),
      source: Object.assign({}, data.source),
      date: Object.assign({}, data.date),
      xaxisTitle: Object.assign({}, data.xaxisTitle),
      yaxisTitle: Object.assign({}, data.yaxisTitle),
      slug: Object.assign({}, data.slug),
      subdekPromo: Object.assign({}, data.subdekPromo),
      sourcePromo: Object.assign({}, data.sourcePromo),
      notePromo: Object.assign({}, data.notePromo),
      chartFont: data.chartFont,
      altText: data.altText,
    },
    xAxisInterval: data.xAxisInterval,
    mobileXAxisInterval: data.mobileXAxisInterval,
    mobileXAxisIntervalSameAsDesktop: data.mobileXAxisIntervalSameAsDesktop,
    yAxisInterval: data.yAxisInterval,
    yAxisMobileInterval: data.yAxisMobileInterval,
    yAxisMobileEnabled: data.yAxisMobileEnabled,
    logScale: data.logScale,
    allowDecimals: data.allowDecimals,
    plotBands: data.plotBands,
    plotBandPresets: data.plotBandPresets,
    majorTicksArticle: data.majorTicksArticle,
    minorTicksArticle: data.minorTicksArticle,
    majorTicksPrint: data.majorTicksPrint,
    minorTicksPrint: data.minorTicksPrint,
    majorTicksPromo: data.majorTicksPromo,
    minorTicksPromo: data.minorTicksPromo,
    hiddenCategoriesArticle: data.hiddenCategoriesArticle,
    hiddenCategoriesPrint: data.hiddenCategoriesPrint,
    hiddenCategoriesPromo: data.hiddenCategoriesPromo,
    dateSpan: data.dateSpan,
    minorLabelsVisible: data.minorLabelsVisible,
    plotLines: data.plotLines,
    colorMode: activeDoc.chart.color.mode,
    horizontalBarType: data.horizontalBarType,
    specialHorizontalBarXValue: data.specialHorizontalBarXValue,
    horizontalBarLabel: data.horizontalBarLabel,
    step: activeDoc.chart.step,
    liveMarketData: data.type === 'market',
    isIntraday: data.isIntraday,
    needsCommas: data.needsCommas,
    isPercent:
      (data.tickers.length > 1 && data.isPercent) ||
      (data.tickers.length === 1 && data.singleIsPercent),
    singleIsPercent: data.singleIsPercent,
    tickers: data.tickers,
    dateRange: data.dateRange,
    start: data.start,
    end: data.end,
    isRolling: data.isRolling,
    frequency: data.frequency,
    showMinorTicks: data.showMinorTicks,
    showTooltips: data.showTooltips,
    connectEmptyDataPoints: data.connectEmptyDataPoints,
    markedDataPoints: data.markedDataPoints,
    categoriesAreDates,
    categoriesAreMonthly: categoriesAreDates && data.categoriesAreMonthly,
    fiscalYears: categoriesAreDates && data.fiscalYears,
    quarterlyResults: categoriesAreDates && data.quarterlyResults,
    irregularDates:
      (categoriesAreDates && data.irregularDates) ||
      (data.type === 'market' && activeDoc.chart.type !== 'bar'),
    reverseDateMonth: categoriesAreDates && !!data.reverseDateMonth,
    removeOverlappingCategories: !categoriesAreDates && data.removeOverlappingCategories,
    allowCategoriesLinebreak: !categoriesAreDates && data.allowCategoriesLinebreak,
    suppress: prepareDatetimeSuppression(data),
    el: el || null,
    type: activeDoc.chart.step || activeDoc.chart.type === 'market' ? 'line' : activeDoc.chart.type,
    product,
    theme: activeDoc.chart.theme,
    width: width || (size ? activeDoc.chart.sizes[size].width : 480),

    sizes: prepareSizes(activeDoc.chart.sizes),
    size,
    print,

    defaults: {
      colors: colorsForProduct(product),
      sizes: getSizesArray(size, product, publish),
    },
  };

  if (print) {
    const printSize = getPrintSize(size, activeDoc.chart.sizes[size], activeDoc.chart.manualHeight);
    opts.height = printSize.height;
    opts.sizes[size].height = printSize.height;
  }
  if (activeDoc.invalidated) {
    opts.invalidated = getInvalidated(activeDoc);
  }
  return opts;
}

function getInvalidated(activeDoc) {
  return activeDoc.invalidated.asMutable
    ? activeDoc.invalidated.asMutable({ deep: true })
    : activeDoc.invalidated;
}

function getProduct(product) {
  return product.indexOf('wsj') === 0 ? 'wsj' : product;
}

function prepareSizes(docSizes) {
  const sizes = docSizes.asMutable ? docSizes.asMutable({ deep: true }) : docSizes;
  Object.keys(sizes).forEach((key) => {
    const size = sizes[key];
    const maxYAxisValue = +size.axes.maxYAxisValue;
    const minYAxisValue = +size.axes.minYAxisValue;
    size.axes.maxYAxisValue = isNaN(maxYAxisValue) ? undefined : maxYAxisValue;
    size.axes.minYAxisValue = isNaN(minYAxisValue) ? undefined : minYAxisValue;
    if (size.axes.maxYAxisValue !== undefined && size.axes.minYAxisValue !== undefined) {
      size.axes.maxYAxisValue = Math.max(size.axes.maxYAxisValue, size.axes.minYAxisValue);
    }
  });
  return sizes;
}

// Get an array of size templates for the renderer to choose amongst, either by
// ID (if one is provided) or by closest width.
function getSizesArray(id, product, publish = 'inset') {
  if (id) {
    let size = getSizeById(id);
    if (size) {
      return [size];
    }
  }
  const sizes = [];
  const TARGETS = getTargets();

  TARGETS.forEach((targets) => {
    if (targets.value === product || (targets.value === 'wsjArticle' && product === 'wsj')) {
      targets.sizes.forEach((size) => {
        if (size.publish === publish || size.publish.indexOf(publish) !== -1) {
          sizes.push(size);
        }
      });
    }
  });
  return sizes;
}

export function getPrintSize(id, size, manualHeight = false) {
  const grid = getGrid(id);
  if (Object.keys(size).length === 1) {
    if (size.columns) return { width: grid.columns[size.columns - 1].toFixed(1) };
    if (size.height) return { lines: Math.round(size.height / grid.line) };
    else return { height: (grid.line * size.lines).toFixed(1) };
  }
  return {
    width: grid.columns[size.columns - 1].toFixed(1),
    height: manualHeight ? size.height : (grid.line * size.lines).toFixed(1),
  };
}

// Package the data up into a format that can be consumed by the renderer,
// removing data/series that is actually categories or series names and not
// numerical chart data.
function prepareRenderData(chart) {
  const { data, color, type, product } = chart;
  const thresholdChart = color.mode === 'THRESHOLD';
  const isScatter = type === 'scatter';
  if (Array.isArray(data.series)) {
    let dataSeries = data.series.asMutable ? data.series.asMutable({ deep: true }) : data.series;
    let renderData = dataSeries.map((series) => {
      const {
        name,
        symbol,
        markdown,
        showInLegend,
        line,
        negativeColor,
        thresholdAbove,
        thresholdBelow,
        decimals,
        prefix,
        suffix,
        tickerType,
        initialIndex,
      } = series;

      const accountForRow = isScatter
        ? series.data
        : series.data.slice(data.firstRowAsNames ? 1 : 0);
      const negativeColorValue = negativeColor || thresholdBelow || series.color;
      const negativeColorIdx = getColorIndex(negativeColorValue, product);
      const seriesColor = getSeriesColor(series, isScatter, thresholdChart, color.opacity);
      const colorIndex = getColorIndex(series.color, product, type);

      return {
        name,
        symbol: symbol || '',
        markdown: markdown || '{series}',
        decimals,
        prefix,
        suffix,
        tickerType,
        showInLegend,
        data: accountForRow,
        dashStyle: line || 'solid',
        opacity: color.opacity,
        colorIndex,
        color: seriesColor,
        negativeColor: negativeColorValue,
        negativeColorIdx,
        initialIndex,
      };
    });

    if (data.firstColumnAsCategories) {
      renderData = renderData.slice(1);
    }

    renderData.forEach((series) => {
      let spliceIndex = -1;
      series.data.forEach((point, index) => {
        if (typeof point.y === 'string') {
          point.y = sanitizeYValue(point.y);
          if (isNaN(point.y)) {
            // console.log(match);
          }
        }
        const colorProp = !isNaN(point.y) && point.y < 0 ? 'negativeColor' : 'color';
        if ((thresholdChart && point.color === series[colorProp]) || point.color === series.color) {
          point.color = undefined;
          if (point.colorIndex) point.colorIndex = undefined;
        }
        if (point.color) {
          point.colorIndex = getColorIndex(point.color, product);
        }
        if (spliceIndex === -1 && isNaN(point.y)) {
          spliceIndex = index;
        }
      });
      if (spliceIndex !== -1) {
        series.data = series.data.slice(0, spliceIndex);
      }
    });

    return renderData;
  }
  return null;
}

function getColorIndex(seriesColor, product, type) {
  const validatedProduct = getProduct(product);
  const productColors = colorsForProduct(validatedProduct);
  const colors = getColors(product, type, productColors);
  const index = colors.findIndex((color) => color === seriesColor);
  return index;
}

function getSeriesColor(series, isScatter, thresholdChart, opacity) {
  const { color, thresholdAbove } = series;
  if (isScatter) {
    return convertToRGBA(color, opacity);
  }
  if (thresholdChart) {
    return thresholdAbove || color;
  }
  return color;
}

function prepareCategories(data) {
  if (data.categories) {
    if (
      data.type === 'series' ||
      data.type === 'market' ||
      data.type === 'econ' ||
      data.firstColumnAsCategories
    ) {
      const cats = data.categories.asMutable ? data.categories.asMutable() : data.categories;
      return cats.slice();
    }
  }
  return null;
}

function prepareDatetimeSuppression(data) {
  if (data.suppress) {
    return data.suppress.asMutable ? data.suppress.asMutable() : data.suppress;
  }
  return null;
}

export const createOptionsForSize = ({ options, size, id }) => {
  const sizeOptions = copyObject(options);
  const missingProps = ['width', 'height'];
  missingProps.forEach((prop) => (sizeOptions[prop] = size[prop]));
  sizeOptions.size = id;
  sizeOptions.print = hasPrint(id);
  sizeOptions.defaults.sizes = getSizesArray(id, sizeOptions.target, sizeOptions.publish);
  return sizeOptions;
};

function updateSeriesForStackedPrintCharts(options) {
  const sizeOptions = copyObject(options);
  sizeOptions.series.forEach((s) => {
    s.data.forEach((dataPoint) => {
      if (dataPoint.y === null) dataPoint.y = 0;
    });
  });
  return sizeOptions;
}

export function updateOptionsForPrint({ options, size, id }) {
  const sizeOptions = createOptionsForSize({ options, size, id });
  if (
    id === 'print-wsj' &&
    (options.type === 'bar' || options.type === 'column') &&
    options.sizes['print-wsj'].stacking
  ) {
    return updateSeriesForStackedPrintCharts(sizeOptions);
  }
  return sizeOptions;
}

/**
 * A utility function that returns appropriate color palette for a given product/chart type.
 * @param {Publications} publication
 * @param {ChartType} chartType
 * @param {[]|{}} productColors - an array for non-wsj publications or obj for wsj
 * @return {[]} - an array of colors
 */
function getColors(publication, chartType, productColors) {
  switch (publication) {
    case 'wsj':
      return [
        ...getWsjColorArr(productColors),
        ...getWsjColorArr(ELECTION_COLORS),
        ...WSJ_VIDEO.theme,
      ];
    default:
      return chartType === 'pie' ? PIE_COLORS[publication] : productColors;
  }
}
